/**
 * Team invite links carry a single `code` query parameter. That code is the team secret plus the
 * date the link was generated, joined with a separator and base64url-encoded. When the code is
 * used, the embedded date must be today, yesterday or tomorrow (UTC), otherwise the link has
 * expired and the person needs a freshly generated one.
 */

const SEPARATOR = '.';
const DATE_TOLERANCE_DAYS = 1;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

type TeamInviteCodeValidation =
    | { status: 'valid'; secret: string }
    | { status: 'expired'; secret: null }
    | { status: 'invalid'; secret: null };

const toBase64Url = (value: string) => {
    return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (value: string) => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);

    return atob(`${base64}${padding}`);
};

const toUtcDateString = (date: Date) => date.toISOString().slice(0, 10);

const toUtcMidnight = (date: string) => Date.parse(`${date}T00:00:00.000Z`);

const TeamInviteCodeHelpers = {
    /**
     * Build the invite code for a team secret, stamped with the given date (defaults to now).
     */
    encode: (secret: string, now: Date = new Date()) => {
        return toBase64Url(`${secret}${SEPARATOR}${toUtcDateString(now)}`);
    },

    /**
     * Split an invite code back into its secret and date. Returns `null` for anything malformed.
     */
    decode: (code: string): { secret: string; date: string } | null => {
        try {
            const decoded = fromBase64Url(code);
            const separatorIndex = decoded.lastIndexOf(SEPARATOR);

            if (separatorIndex <= 0) {
                return null;
            }

            const secret = decoded.slice(0, separatorIndex);
            const date = decoded.slice(separatorIndex + 1);

            if (!DATE_PATTERN.test(date) || Number.isNaN(toUtcMidnight(date))) {
                return null;
            }

            return { secret, date };
        } catch {
            return null;
        }
    },

    /**
     * Whether the embedded date is within the accepted window around `now` (today ± 1 day, UTC).
     */
    isDateWithinWindow: (date: string, now: Date = new Date()) => {
        const stamped = toUtcMidnight(date);
        const today = toUtcMidnight(toUtcDateString(now));

        if (Number.isNaN(stamped)) {
            return false;
        }

        return Math.abs(stamped - today) / DAY_IN_MS <= DATE_TOLERANCE_DAYS;
    },

    /**
     * Validate an invite code. Only a `valid` result exposes the team secret for further checks.
     */
    validate: (code: string | null | undefined, now: Date = new Date()): TeamInviteCodeValidation => {
        const decoded = code ? TeamInviteCodeHelpers.decode(code) : null;

        if (!decoded) {
            return { status: 'invalid', secret: null };
        }

        if (!TeamInviteCodeHelpers.isDateWithinWindow(decoded.date, now)) {
            return { status: 'expired', secret: null };
        }

        return { status: 'valid', secret: decoded.secret };
    },
};

export { TeamInviteCodeHelpers };
export type { TeamInviteCodeValidation };
